import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Lightning, Gauge, Rocket } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const WebsiteSpeedPortfolio = () => {
  const { t } = useTranslation();

  const portfolioItems = [
    {
      key: 'speed1',
      title: 'E-commerce Speed Boost',
      description: 'Website snelheidsoptimalisatie voor een webshop die resulteerde in 80% snellere laadtijden en 40% meer conversies.',
      category: 'E-commerce Speed',
      technologies: ['Image Optimization', 'Code Minification', 'CDN Setup', 'Caching Strategy'],
      features: ['80% Snellere Laadtijd', '40% Meer Conversies', 'Core Web Vitals 95+', 'Mobile First'],
      link: '#'
    },
    {
      key: 'speed2',
      title: 'Corporate Website Performance',
      description: 'Performance optimalisatie voor een corporate website die nu in minder dan 2 seconden laadt op alle apparaten.',
      category: 'Corporate Speed',
      technologies: ['Performance Audit', 'Asset Optimization', 'Server Optimization', 'Monitoring Setup'],
      features: ['<2s Laadtijd', '99/100 PageSpeed Score', 'Mobile Optimized', 'SEO Boost'],
      link: '#'
    },
    {
      key: 'speed3',
      title: 'Blog Platform Acceleration',
      description: 'Snelheidsoptimalisatie voor een blog platform dat 3x sneller laadt en 60% meer pageviews genereert.',
      category: 'Blog Speed',
      technologies: ['Lazy Loading', 'Critical CSS', 'Database Optimization', 'CDN Implementation'],
      features: ['3x Snellere Laadtijd', '60% Meer Pageviews', 'Better UX', 'Reduced Bounce Rate'],
      link: '#'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const getCategoryIcon = (category) => {
    if (category.includes('E-commerce')) return <Rocket className="w-5 h-5" weight="thin" />;
    if (category.includes('Corporate')) return <Gauge className="w-5 h-5" weight="thin" />;
    return <Lightning className="w-5 h-5" weight="thin" />;
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Onze <span className="text-blue-600">snelheidsresultaten</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Van e-commerce tot corporate websites. Bekijk enkele van onze snelheidsoptimalisatie successen 
            en laat je inspireren door de performance verbeteringen die we behalen.
          </p>
        </motion.div>

        {/* Portfolio Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
        >
          {portfolioItems.map((item, index) => (
            <motion.div
              key={item.key}
              variants={itemVariants}
              className="group"
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                {/* Project Header */}
                <div className="p-8">
                  {/* Category Badge */}
                  <div className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                    {getCategoryIcon(item.category)}
                    <span className="ml-2">{item.category}</span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{item.description}</p>

                  {/* Technologies */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Optimalisatie Technieken:</h4>
                    <div className="flex flex-wrap gap-2">
                      {item.technologies.map((tech, techIndex) => (
                        <span 
                          key={techIndex}
                          className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Key Results */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Behaalde resultaten:</h4>
                    <div className="space-y-2">
                      {item.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-3xl p-12 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Wil je ook zulke snelheidsresultaten?</h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Laat ons jouw website razendsnel maken. Van performance analyse tot code optimalisatie 
              - we zorgen voor meetbare snelheidsverbeteringen.
            </p>
            <Link to="/start-your-project" className="inline-flex items-center bg-blue-600 text-white px-10 py-4 rounded-2xl font-semibold hover:bg-blue-700 transition-colors">
              <span>Start je speed project</span>
              <ArrowRight className="w-5 h-5 ml-2" weight="thin" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WebsiteSpeedPortfolio;
